import { useEffect, useState } from "react";
import * as BosWebGuiServices from "data/BosWebServices/BosWebGuiServices";
import * as BosIdentityServices from "data/BosWebServices/BosIdentityServices";

// Redux
import { useSelector, useDispatch } from "react-redux";
import * as dataSegmentActions from "redux/actions/dataSegmentActions";
import * as sessionActions from "redux/actions/sessionActions";
import * as appStateActions from "redux/actions/appStateActions";

let globalLastClosedSessionId;

export const useAssureBosSessionFromUrl = (fullUri, keep, languageChangedTrigger) => {
  const [session, setSession] = useState();
  const [dataSegment, setDataSegment] = useState();
  const [userError, setUserError] = useState();
  const [connectionError, setConnectionError] = useState();

  const bosUserAuthToken = useSelector(state => state.bosUser.authToken);
  const configDataAuthToken = useSelector(state => state.dataSegments.configDataSegment.authToken);
  const configDataSegmentId = useSelector(state => state.dataSegments.configDataSegment.dataSegmentId);
  const openSessions = useSelector(state => state.sessions.openSessions);
  const currentSessionId = useSelector(state => state.sessions.currentSessionId);
  const currentDataSegment = useSelector(state => state.dataSegments.currentDataSegment);

  const dispatch = useDispatch();

  async function fetchData(previousSessionUri) {
    try {
      let useSessionId = await BosWebGuiServices.fetchStartSession(configDataAuthToken);
      console.log("StartSession result: ", useSessionId);

      console.log("ProcessUrl with sessionId:", useSessionId);
      let result = await BosWebGuiServices.fetchProcessUrl(configDataAuthToken, useSessionId, fullUri);
      console.log("ProcessUrl result:", result);

      let isWorkspace = result.endpointTechnicalName === "Workspace";
      let subjectTitle = result.subjectTitle;
      let imageRef = result.subjectImageRef;

      let dataSegmentId = isWorkspace ? result.subjectDataSegmentId : result.servingDataSegmentId;
      let dataSegmentTechnicalName = isWorkspace ? result.subjectDataSegmentTechnicalName : result.servingDataSegmentTechnicalName;
      let dataSegmentDisplayName = isWorkspace ? result.subjectDataSegmentDisplayName : result.servingDataSegmentDisplayName;

      let solutionSegmentId = isWorkspace ? result.subjectSolutionSegmentId : result.servingSolutionSegmentId;
      let solutionSegmentTechnicalName = isWorkspace ? result.subjectSolutionSegmentTechnicalName : result.servingSolutionSegmentTechnicalName;
      let solutionSegmentDisplayName = isWorkspace ? result.subjectSolutionSegmentDisplayName : result.servingSolutionSegmentDisplayName;

      let closeDestinationUri = previousSessionUri || result.subjectSegmentUrlPart;
      let dataSegmentUri = result.subjectSegmentUrlPart;
      let sessionDisplayName = result.taskId ? result.taskDisplayName : dataSegmentDisplayName;

      let useDataSegmentAuthToken = (currentDataSegment.id === dataSegmentId) ? currentDataSegment.authToken : null;
      if (!useDataSegmentAuthToken) {
        let authorizeResult = await BosIdentityServices.fetchAuthorize(bosUserAuthToken, configDataSegmentId, dataSegmentId);
        useDataSegmentAuthToken = authorizeResult.token;
        if (keep) {
          dispatch(
            dataSegmentActions.setDataSegment({
              newAuthToken: useDataSegmentAuthToken,
              newId: dataSegmentId,
              newDisplayName: dataSegmentDisplayName,
              newTechnicalName: dataSegmentTechnicalName,
              newSolutionId: solutionSegmentId,
              newSolutionDisplayName: solutionSegmentDisplayName,
              newSolutionTechnicalName: solutionSegmentTechnicalName,
              newOriginUri: dataSegmentUri,
              newTitle: subjectTitle,
              newImageRef: imageRef,
            })
          );
          dispatch(
            appStateActions.setUILanguage(authorizeResult.uiLanguageISOCode)
          );
          dispatch(
            appStateActions.setDataViewLanguage(authorizeResult.dataViewLanguageISOCode)
          );
        }
      }
      setDataSegment({
        authToken: useDataSegmentAuthToken,
        id: dataSegmentId,
        displayName: dataSegmentDisplayName,
        technicalName: dataSegmentTechnicalName,
        solutionId: solutionSegmentId,
        solutionDisplayName: solutionSegmentDisplayName,
        solutionTechnicalName: solutionSegmentTechnicalName,
        originUri: dataSegmentUri,
        title: subjectTitle,
        imageRef: imageRef,
      });

      if (keep) {
        dispatch(
          sessionActions.addSession({
            newId: useSessionId,
            newDisplayName: sessionDisplayName,
            newOriginUri: fullUri,
            newCloseDestinationUri: closeDestinationUri,
            newDataSegmentId: dataSegmentId,
            newTaskId: result.taskId,
            newTaskDisplayName: result.taskDisplayName,
            newIsWorkspace: isWorkspace
          })
        );
      }
      setSession({
        id: useSessionId,
        displayName: sessionDisplayName,
        originUri: fullUri,
        closeDestinationUri: closeDestinationUri,
        dataSegmentId: dataSegmentId,
        taskId: result.taskId,
        taskDisplayName: result.taskDisplayName,
        isWorkspace: isWorkspace
      });

      dispatch(
        appStateActions.showAppTitle(isWorkspace)
      );

      if (result.errorMessage) setUserError(result.errorMessage);
      else setUserError();

    } catch (error) {
      console.error("assureBosSessionFromUrl error:", error);
      setSession();
      setDataSegment();
      setUserError();
      setConnectionError(error.toString());
    }
  }

  async function fetchDataForUpdate() {
    try {
      let useSessionId = session.id;
      console.log("StartSession result: ", useSessionId);

      console.log("ProcessUrl with sessionId:", useSessionId);
      let result = await BosWebGuiServices.fetchProcessUrl(configDataAuthToken, useSessionId, fullUri);
      console.log("ProcessUrl result:", result);

      let isWorkspace = result.endpointTechnicalName === "Workspace";
      let subjectTitle = result.subjectTitle;

      let dataSegmentId = isWorkspace ? result.subjectDataSegmentId : result.servingDataSegmentId;
      let dataSegmentDisplayName = isWorkspace ? result.subjectDataSegmentDisplayName : result.servingDataSegmentDisplayName;

      let solutionSegmentDisplayName = isWorkspace ? result.subjectSolutionSegmentDisplayName : result.servingSolutionSegmentDisplayName;

      let sessionDisplayName = result.taskId ? result.taskDisplayName : dataSegmentDisplayName;

      if (keep) {
        dispatch(
          dataSegmentActions.updateDataSegment({
            dataSegmentId: dataSegmentId,
            displayName: dataSegmentDisplayName,
            solutionDisplayName: solutionSegmentDisplayName,
            title: subjectTitle
          })
        );
      }
      setDataSegment(prev => ({
        ...prev,
        dataSegmentId: dataSegmentId,
        displayName: dataSegmentDisplayName,
        solutionDisplayName: solutionSegmentDisplayName,
        title: subjectTitle
      }));

      if (keep) {
        dispatch(
          sessionActions.updateSession({
            sessionId: useSessionId,
            displayName: sessionDisplayName,
            taskDisplayName: result.taskDisplayName,
          })
        );
      }
      setSession(prev => ({
        ...prev,
        sessionId: useSessionId,
        displayName: sessionDisplayName,
        taskDisplayName: result.taskDisplayName,
      }));

      if (result.errorMessage) setUserError(result.errorMessage);
      else setUserError();
    } catch (error) {
      console.error("assureBosSessionFromUrl error:", error);
    }
  }

  useEffect(() => {
    if (fullUri) {
      let possibleCurrentSession = openSessions.find(item => item && item.originUri.toLowerCase() === fullUri.toLowerCase());
      if (possibleCurrentSession) {
        console.log("Detected already open Uri...");
        setSession(possibleCurrentSession);
        setDataSegment(currentDataSegment);
        setUserError();
      } else {
        let realCurrentSession = openSessions.find(item => item.id === currentSessionId);
        let previousSessionUri = realCurrentSession ? realCurrentSession.originUri : null;
        fetchData(previousSessionUri);
      }
    }
  }, [fullUri]);

  useEffect(() => {
    if (session) {
      fetchDataForUpdate();
    }
  }, [languageChangedTrigger]);

  return {
    session,
    dataSegment,
    userError,
    connectionError
  };
};


export const closeSession = async (sessionActions, dataSegmentAuthToken, session) => {
  if (session) {
    if (sessionActions) sessionActions.removeSession(session.id);
    try {
      console.log("SessionClose", session.id);
      globalLastClosedSessionId = session.id;
      await BosWebGuiServices.fetchCloseSession(dataSegmentAuthToken, session.id);
    } catch (error) {
      console.error("SessionClose error: ", error);
    }
  }
};

export const leaveSession = async (dataSegmentAuthToken, session) => {
  if (session) {
    if (globalLastClosedSessionId === session.id) {
      console.log("SessionLeave skipped, since it was closed before.", session.id);
      return;
    }
    try {
      console.log("SessionLeave", session.id);
      await BosWebGuiServices.fetchLeaveSession(dataSegmentAuthToken, session.id);
    } catch (error) {
      console.error("SessionLeave error: ", error);
    }
  }
};

export const navigatAfterCloseSession = (history, session) => {
  if (session) {
    let destination = session.closeDestinationUri;
    if (!destination.startsWith("/")) destination = "/" + destination;
    console.log("Session close navigation with closeDestinationUri", session.id, destination);
    history.push(destination);
  }
};